/**
 * Follow-ups Admin API client + pure helpers — feeds FollowUpsAdminPage
 * (CVG3-FU / L106, 2026-07-11).
 *
 * Backend surface (telecaller.routes.ts followups region, same converge):
 *   - GET  /telecaller/followups?scope=team&agentId&status&overdue&startDate&endDate&stage
 *       rows carry createdBy/agent {id,name} (L105 serializer) + leadId/leadStage
 *   - GET  /telecaller/followups/delegable-users → reassign roster (role-ruled)
 *   - PATCH /telecaller/followups/:id { agentId | status | scheduledAt | notes }
 *       agentId change = role-ruled reassign (403 FU_DELEGATION_FORBIDDEN)
 *
 * Pure helpers (computeFollowUpKpis / setByLabel / contactLabel) are
 * unit-tested in followUpsAdminApi.test.ts.
 */
import { api } from '@/utils/api';
import { joinPersonName } from '@/utils/names';

// ─── Types (mirror the followups serializer) ─────────────────────────────────

export interface FollowUpUserRef {
  id: string;
  name: string;
}

export interface FollowUpContactRef {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  mobile?: string | null;
}

export interface FollowUpRow {
  id: string;
  contactId: string;
  agentId: string;
  scheduledAt: string | null;
  completedAt?: string | null;
  status: string; // PENDING | COMPLETED | MISSED | CANCELLED
  notes?: string | null;
  priority?: number;
  createdById?: string | null;
  assignedAt?: string | null;
  createdBy?: FollowUpUserRef | null;
  agent?: FollowUpUserRef | null;
  contact?: FollowUpContactRef | null;
  leadId?: string | null;
  leadStage?: string | null;
}

export interface DelegableUser {
  id: string;
  name: string;
  roleName?: string | null;
  isSelf?: boolean;
}

export interface FollowUpListFilters {
  agentId?: string;
  status?: string;
  overdue?: boolean;
  startDate?: string;
  endDate?: string;
  stage?: string;
  page?: number;
  limit?: number;
}

// ─── Fetchers ────────────────────────────────────────────────────────────────

export async function fetchTeamFollowUps(
  f: FollowUpListFilters = {},
): Promise<{ rows: FollowUpRow[]; total: number }> {
  const params: Record<string, string | number> = {
    scope: 'team',
    page: f.page ?? 1,
    limit: f.limit ?? 100,
  };
  if (f.agentId) params.agentId = f.agentId;
  if (f.status) params.status = f.status;
  if (f.overdue) params.overdue = '1';
  if (f.startDate) params.startDate = f.startDate;
  if (f.endDate) params.endDate = f.endDate;
  if (f.stage) params.stage = f.stage;
  const res = await api.get('/telecaller/followups', { params });
  return {
    rows: (res.data?.data ?? []) as FollowUpRow[],
    total: res.data?.pagination?.total ?? 0,
  };
}

export interface OverdueFollowUpCount {
  total: number;
  leadFollowUps: number;
  callFollowUps: number;
}

/**
 * Team overdue count from the SAME server definition as the Team Sales card
 * (open-lead follow-up dates + PENDING call follow-ups, due before now,
 * viewer's team). Not derived from this page's 100-row window.
 */
export async function fetchOverdueFollowUpCount(): Promise<OverdueFollowUpCount> {
  const res = await api.get('/analytics/sales/overdue-followups', { params: { scope: 'TEAM' } });
  const d = res.data?.data;
  if (res.data?.success !== true || typeof d?.value !== 'number') throw new Error('Overdue count failed');
  return { total: d.value, leadFollowUps: d.meta?.leadFollowUps ?? 0, callFollowUps: d.meta?.callFollowUps ?? 0 };
}

export async function fetchDelegableUsers(): Promise<DelegableUser[]> {
  const res = await api.get('/telecaller/followups/delegable-users');
  return (res.data?.data?.users ?? []) as DelegableUser[];
}

export async function reassignFollowUp(id: string, agentId: string): Promise<void> {
  await api.patch(`/telecaller/followups/${id}`, { agentId });
}

export async function completeFollowUp(id: string): Promise<void> {
  await api.patch(`/telecaller/followups/${id}`, { status: 'COMPLETED' });
}

export async function cancelFollowUp(id: string): Promise<void> {
  await api.patch(`/telecaller/followups/${id}`, { status: 'CANCELLED' });
}

// ─── Pure helpers (unit-tested) ──────────────────────────────────────────────

/** Display name for the row's contact — name → phone → short id. */
export function contactLabel(row: FollowUpRow): string {
  const c = row.contact;
  const joined = joinPersonName(c?.firstName, c?.lastName);
  if (joined) return joined;
  const phone = (c?.mobile ?? c?.phone ?? '').trim();
  if (phone) return phone;
  return row.contactId.length >= 6
    ? `Lead · ${row.contactId.slice(-6)}`
    : row.contactId || '—';
}

/**
 * "Set by «name»" column value (L105 3c): non-null only when the follow-up
 * was set by someone OTHER than its agent (createdById != agentId, both
 * non-null) and the serializer enriched a creator name. Legacy/system rows
 * (createdById null) → null.
 */
export function setByLabel(row: FollowUpRow): string | null {
  if (!row.createdById || !row.agentId) return null;
  if (row.createdById === row.agentId) return null;
  const name = (row.createdBy?.name ?? '').trim();
  return name || null;
}

export interface FollowUpKpis {
  dueToday: number;
  overdue: number;
  completedThisWeek: number;
}

/**
 * KPI strip (L106 3d): due today / overdue / completed-this-week.
 *  - dueToday: PENDING, scheduled on the current calendar day
 *  - overdue: PENDING, scheduled BEFORE the start of the current day
 *    (disjoint from dueToday — earlier-today rows count as due today)
 *  - completedThisWeek: COMPLETED with completedAt in the trailing 7 days
 */
export function computeFollowUpKpis(rows: FollowUpRow[], now: Date = new Date()): FollowUpKpis {
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  let dueToday = 0;
  let overdue = 0;
  let completedThisWeek = 0;
  for (const r of rows) {
    if (r.status === 'PENDING' && r.scheduledAt) {
      const at = new Date(r.scheduledAt);
      if (Number.isNaN(at.getTime())) continue;
      if (at >= dayStart && at < dayEnd) dueToday += 1;
      else if (at < dayStart) overdue += 1;
    } else if (r.status === 'COMPLETED') {
      const doneRaw = r.completedAt ?? null;
      if (!doneRaw) continue;
      const done = new Date(doneRaw);
      if (!Number.isNaN(done.getTime()) && done >= weekAgo && done <= now) {
        completedThisWeek += 1;
      }
    }
  }
  return { dueToday, overdue, completedThisWeek };
}
